import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { PaperPlaneTilt, WifiHigh, WifiSlash, Camera, Paperclip, ArrowBendUpLeft, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ChatSidebar } from '../components/chat/ChatSidebar';
import { ChatMessages } from '../components/chat/ChatMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const ChatPage = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState('detente');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [chatUsers, setChatUsers] = useState([]);
  const [showNewPrivate, setShowNewPrivate] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState('sidebar'); // 'sidebar' | 'chat'

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const fetchChannels = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/chat/channels`, { withCredentials: true }); setChannels(res.data); }
    catch (e) { console.error('Erreur chargement canaux:', e); }
  }, []);

  const fetchMessages = useCallback(async (ch) => {
    try {
      const res = await axios.get(`${API_URL}/api/chat/messages?channel=${ch || activeChannel}`, { withCredentials: true });
      setMessages(res.data);
      setLoading(false);
    } catch (e) { console.error('Erreur chargement messages:', e); setLoading(false); }
  }, [activeChannel]);

  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/chat/users`, { withCredentials: true }); setChatUsers(res.data); }
    catch (e) { console.error('Erreur chargement utilisateurs:', e); }
  }, []);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const token = localStorage.getItem('batiboys_access_token') || '';
    const ws = new WebSocket(`${WS_URL}/api/chat/ws?token=${encodeURIComponent(token)}`);
    ws.onopen = () => { setWsConnected(true); pingIntervalRef.current = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000); };
    ws.onclose = () => { setWsConnected(false); clearInterval(pingIntervalRef.current); reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000); };
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message' && data.data) {
          setMessages(prev => (data.channel === activeChannel || data.data.channel === activeChannel) ? [...prev, data.data] : prev);
          if (data.data?.channel?.startsWith('private_')) fetchChannels();
        } else if (data.type === 'message_deleted') {
          setMessages(prev => prev.filter(m => m.id !== data.message_id));
        } else if (data.type === 'typing' && (data.channel === activeChannel || !data.channel)) {
          setTypingUsers(prev => ({ ...prev, [data.user_id]: { name: data.user_name, ts: Date.now() } }));
        }
      } catch (e) { console.error('WS parse error:', e); }
    };
    wsRef.current = ws;
  }, [activeChannel, fetchChannels]);

  useEffect(() => { fetchChannels(); fetchUsers(); connectWebSocket(); return () => { clearTimeout(reconnectTimeoutRef.current); clearInterval(pingIntervalRef.current); if (wsRef.current) wsRef.current.close(); }; }, [fetchChannels, fetchUsers, connectWebSocket]);
  useEffect(() => { fetchMessages(activeChannel); }, [activeChannel, fetchMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { const i = setInterval(() => { setTypingUsers(prev => { const now = Date.now(); const next = {}; for (const [uid, val] of Object.entries(prev)) { if (now - val.ts < 3000) next[uid] = val; } return Object.keys(next).length !== Object.keys(prev).length ? next : prev; }); }, 1000); return () => clearInterval(i); }, []);

  const sendTypingEvent = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000 && wsRef.current?.readyState === WebSocket.OPEN) { wsRef.current.send(`typing:${activeChannel}`); lastTypingSentRef.current = now; }
  }, [activeChannel]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const payload = { content: newMessage.trim(), channel: activeChannel };
      if (replyTo) { payload.reply_to_id = replyTo.id; payload.reply_to_name = replyTo.sender_name; payload.reply_to_content = replyTo.content?.substring(0, 80); }
      const res = await axios.post(`${API_URL}/api/chat/messages`, payload, { withCredentials: true });
      setMessages(prev => [...prev, res.data]);
      setNewMessage(''); setReplyTo(null);
    } catch (e) { toast.error('Erreur envoi'); }
    finally { setSending(false); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('channel', activeChannel);
    try { const res = await axios.post(`${API_URL}/api/chat/upload`, fd, { withCredentials: true }); setMessages(prev => [...prev, res.data]); }
    catch (e) { toast.error('Erreur upload'); }
  };

  const handleDeleteMessage = (msgId) => {
    setMessages(prev => prev.filter(m => m.id !== msgId));
  };

  const switchChannel = (chId) => {
    setActiveChannel(chId); setReplyTo(null); setTypingUsers({});
    if (isMobile) setMobileView('chat');
  };

  const startPrivateChat = (otherUser) => {
    const ids = [user.id, otherUser.id].sort();
    const channelId = `private_${ids[0]}_${ids[1]}`;
    if (!channels.find(c => c.id === channelId)) setChannels(prev => [...prev, { id: channelId, name: otherUser.name, type: 'private', other_user_id: otherUser.id }]);
    switchChannel(channelId); setShowNewPrivate(false);
  };

  const handleDeleteConversation = async (channelId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try { await axios.delete(`${API_URL}/api/chat/conversations/${channelId}`, { withCredentials: true }); toast.success('Conversation supprimee'); setChannels(prev => prev.filter(c => c.id !== channelId)); if (activeChannel === channelId) { setActiveChannel('detente'); setMessages([]); } }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur suppression'); }
  };

  const activeChInfo = channels.find(c => c.id === activeChannel);
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  // Mobile: show either sidebar or chat
  const showSidebar = !isMobile || mobileView === 'sidebar';
  const showChat = !isMobile || mobileView === 'chat';

  return (
    <div data-testid="chat-page" style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 100px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '8px 12px' : '0 0 12px', flexShrink: 0 }}>
        {!isMobile && <h1 className="page-title" style={{ margin: 0 }}>Chat d'equipe</h1>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', marginLeft: 'auto' }} data-testid="ws-status">
          {wsConnected ? <><WifiHigh size={14} style={{ color: '#2E7D32' }} /> <span style={{ color: '#2E7D32' }}>Connecte</span></> : <><WifiSlash size={14} style={{ color: '#C62828' }} /> <span style={{ color: '#C62828' }}>Reconnexion...</span></>}
        </div>
      </div>

      {/* Main chat area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', borderRadius: isMobile ? '0' : '8px', border: isMobile ? 'none' : '1px solid #E2E2E6', backgroundColor: 'white' }}>

        {/* Sidebar */}
        {showSidebar && (
          <ChatSidebar
            channels={channels} activeChannel={activeChannel}
            onSwitchChannel={switchChannel}
            onShowNewPrivate={() => setShowNewPrivate(true)}
            onDeleteConversation={handleDeleteConversation}
            isMobile={isMobile}
          />
        )}

        {/* Chat panel */}
        {showChat && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            <ChatMessages
              messages={messages} activeChannel={activeChannel} activeChInfo={activeChInfo}
              userId={user?.id} typingUsers={typingUsers} setReplyTo={setReplyTo}
              messagesEndRef={messagesEndRef} onDeleteMessage={handleDeleteMessage}
              isMobile={isMobile}
              onBackToSidebar={() => setMobileView('sidebar')}
            />

            {/* Reply bar */}
            {replyTo && (
              <div style={{ padding: '6px 12px', borderTop: '1px solid #E2E2E6', backgroundColor: '#F8F9FF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, fontSize: '0.8rem' }}>
                <div style={{ color: '#5E5F66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <ArrowBendUpLeft size={12} /> Repondre a <strong>{replyTo.sender_name}</strong>: {replyTo.content?.substring(0, 40)}
                </div>
                <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, padding: '4px' }}><X size={14} /></button>
              </div>
            )}

            {/* Input bar */}
            <form onSubmit={handleSend} style={{
              padding: isMobile ? '8px' : '10px 14px',
              borderTop: '1px solid #E2E2E6',
              display: 'flex', gap: '6px', alignItems: 'center',
              backgroundColor: '#FAFAFA', flexShrink: 0
            }}>
              <button type="button" onClick={() => fileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E5F66', padding: '6px', flexShrink: 0 }} data-testid="attach-btn">
                <Paperclip size={20} />
              </button>
              <label style={{ cursor: 'pointer', color: '#5E5F66', padding: '6px', display: 'flex', flexShrink: 0 }} data-testid="camera-btn">
                <Camera size={20} />
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }} />
              </label>
              <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }} onChange={(e) => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }} />
              <input
                ref={inputRef}
                data-testid="chat-input"
                type="text" className="form-input"
                value={newMessage}
                onChange={(e) => { setNewMessage(e.target.value); if (e.target.value.trim()) sendTypingEvent(); }}
                placeholder="Votre message..."
                style={{ flex: 1, minWidth: 0, fontSize: isMobile ? '16px' : '0.9rem', borderRadius: '20px', padding: '8px 16px' }}
              />
              <button data-testid="send-message-btn" type="submit" className="btn btn-primary" disabled={sending || !newMessage.trim()}
                style={{ borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </form>
          </div>
        )}
      </div>

      {/* New private chat modal */}
      {showNewPrivate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: isMobile ? '90%' : '360px', maxHeight: '70vh', overflow: 'auto' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Nouvelle conversation</h3>
              <button onClick={() => setShowNewPrivate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ padding: '8px' }}>
              {chatUsers.map(u => (
                <button key={u.id} onClick={() => startPrivateChat(u)} data-testid={`start-private-${u.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent', width: '100%', textAlign: 'left', borderRadius: '8px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: u.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>{u.name.charAt(0).toUpperCase()}</div>
                  <div><div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{u.name}</div><div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{u.role === 'admin' ? 'Admin' : 'Salarie'}</div></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
